import { useContext, useState, useEffect } from 'react';
import PageContext from './PageContext';
import WorkflowCommentLog from '../sub-components/WorkflowCommentLogCopy';
import { HttpContext } from '../../api-services/HttpService';
import CCOffcanvas from "./CCOffcanvas.tsx";
import ButtonComponent from "../sub-components/ButtonComponent"
import ApiRequestButton from "../sub-components/ApiRequestButton"
import  '../../css/offcanvasStyle.css'; 
import EllipsisInfoLabel from './EllipsisInfoLabel.jsx';
import OrderedDataTable from './OrderedDataTableCopy';
import RebatePayPeriodTableRow from './RebatePayPeriodTableRow';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthRounded';
import WarningIcon from '@mui/icons-material/Warning';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';

const YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const formatDateToString = (date) => {
    if (!date) return null;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

// parse 'YYYY-MM-DD' as a local date (new Date(str) parses as UTC and can shift the day)
const getValidDate = (dateStr) => {
    if (typeof dateStr !== 'string' || !YMD_REGEX.test(dateStr)) return null;
    const [y, m, d] = dateStr.split('-').map(Number);
    const parsedDate = new Date(y, m - 1, d);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
}

// columns of the bulk pay period table
const PAY_PERIOD_TABLE_HEADER = [
    { columnname: 'workflowid', columntag: 'Rebate ID', columnorder: 0 },
    { columnname: 'mastercompanyname', columntag: 'Rebate Name', columnorder: 1 },
    { columnname: 'payperiod', columntag: 'Pay Period', columnorder: 2 },
];

// function wrapper to pass row component to table
const RebatePayPeriodRowComponent = (row, count, tableHeader) => (
    <RebatePayPeriodTableRow row={row} tableHeader={tableHeader} />
);

const RebatePayoutEditModal = () => {
    // get context variables from parent
    // payPeriodRows: one row when opened from the row pencil icon, many when opened from the bulk action
    const {
        pencilModal,
        setPencilModal,
        payPeriodRows,
        refreshSummaryData,
        snackbarAlert,
        exitPayPeriodMode,
        holidays,
    } = useContext(PageContext);

    const {
        POST,
    } = useContext(HttpContext);

    // initialize state
    const [commentText, setCommentText] = useState('');
    const [payPeriodDate, setPayPeriodDate] = useState(null);
    // message for a weekend/holiday date, set while the confirmation dialog is open
    const [dateWarning, setDateWarning] = useState(null);

    // returns a warning message if the date is a weekend or a holiday, otherwise null
    const getNonBusinessDayMessage = (date) => {
        const dateStr = formatDateToString(date);
        const reasons = [];
        if (date.getDay() === 0) reasons.push('a Sunday');
        if (date.getDay() === 6) reasons.push('a Saturday');
        const holiday = (holidays || []).find(h => h.holidaydate === dateStr);
        if (holiday) reasons.push(`a holiday (${holiday.holidayname})`);
        return reasons.length ? `${dateStr} is ${reasons.join(' and ')}. Do you still want to proceed?` : null;
    }

    const isMulti = payPeriodRows.length > 1;
    const singleRow = payPeriodRows.length === 1 ? payPeriodRows[0] : null;

    // prefill with the current pay period when a single row is edited
    useEffect(() => {
        setPayPeriodDate(singleRow ? getValidDate(singleRow.payperiod) : null);
    }, [payPeriodRows]);

    const closeModal = () => {
        setCommentText('');
        setDateWarning(null);
        setPencilModal(false);
    }

    // update pay period and add a comment for every selected workflow
    const updatePayPeriodAndComment = (workflowids, commenttext, paymentdate) => {
        const path = isMulti ? '/rebate/summary/multicompany/payperiod/edit' : '/rebate/summary/payperiod/edit';
        const payload = isMulti
            ? { workflowids: workflowids, commenttext: commenttext, updatedpaymentdate: paymentdate }
            : { workflowid: workflowids[0], commenttext: commenttext, updatedpaymentdate: paymentdate };
        return POST(path, payload)
                .then(() => {
                    closeModal();
                    // leave bulk selection mode (also clears the selection)
                    exitPayPeriodMode();
                    snackbarAlert(true, 'Successfully updated data.', 'success');
                    refreshSummaryData();
                })
                .catch(err => {
                    console.log(err);
                    snackbarAlert(true, 'Error updating data.', 'error');
                });
    }

    const onSubmitClick = () => {
        // Validate date is selected
        if (!payPeriodDate || isNaN(payPeriodDate.getTime())) {
            snackbarAlert(true, 'Please select a valid pay period date.', 'warning');
            return Promise.reject();
        }

        // single edit: date must differ from the current one
        if (singleRow && payPeriodDate.toDateString() === getValidDate(singleRow.payperiod)?.toDateString()) {
            snackbarAlert(true, 'Please select a different pay period date.', 'warning');
            return Promise.reject();
        }

        // Validate comment text is not empty
        if (!commentText || commentText.trim() === '') {
            snackbarAlert(true, 'Please enter a comment.', 'warning');
            return Promise.reject();
        }

        // warn and ask for confirmation when the date is a weekend or a holiday
        const warning = getNonBusinessDayMessage(payPeriodDate);
        if (warning) {
            setDateWarning(warning);
            return Promise.reject();
        }

        return savePayPeriod();
    }

    // save the picked date and comment for all selected rows
    const savePayPeriod = () => {
        return updatePayPeriodAndComment(payPeriodRows.map(row => row.workflowid), commentText.trim(), formatDateToString(payPeriodDate));
    }

    // user confirmed saving a weekend or holiday date
    const onConfirmDateWarning = () => {
        setDateWarning(null);
        savePayPeriod();
    }

    return (
        <CCOffcanvas
            title={"Edit Pay Period"}
            show={pencilModal}
            onCanvasClose={closeModal}
            bodyCssClassName="offcanvas-body"
            offCanvasClassName="offcanvas-parent-card"
        >
            <div className="d-flex flex-column h-100">
                {/* confirmation shown when the picked date is a weekend or a holiday */}
                <Dialog
                    open={dateWarning !== null}
                    onClose={() => setDateWarning(null)}
                    PaperProps={{ sx: { borderRadius: '8px', minWidth: '400px' } }}
                >
                    {/* header, fonts and colors follow the offcanvas header and snackbar styles */}
                    <DialogTitle sx={{ backgroundColor: '#E5F2F6', fontFamily: 'Roboto Medium', fontWeight: 500, fontSize: '12px', lineHeight: '14px', color: '#000000', padding: '16px' }}>
                        Non-Business Day Selected
                    </DialogTitle>
                    <DialogContent sx={{ padding: '16px !important' }}>
                        <div className="d-flex align-items-center gap-2">
                            <WarningIcon color="warning" />
                            <DialogContentText sx={{ fontFamily: 'Roboto Regular', fontSize: '14px', color: '#001317' }}>
                                {dateWarning}
                            </DialogContentText>
                        </div>
                    </DialogContent>
                    <DialogActions sx={{ padding: '0 16px 16px 16px' }}>
                        <ButtonComponent tag={"Cancel"}
                            border={'#fcd800 1px solid'} background={'#fff'} color={'#001317'}
                            onButtonClick={() => setDateWarning(null)}
                        />
                        <ButtonComponent tag={"Proceed"}
                            border={'#fcd800 1px solid'} background={'#fcd800'} color={'#001317'}
                            onButtonClick={onConfirmDateWarning}
                        />
                    </DialogActions>
                </Dialog>
                {/* read only info on the rebate workflow(s) being edited */}
                <div className="bg-white p-3" style={{fontSize: '14px'}}>
                    {isMulti ?
                        <div>{payPeriodRows.length} Companies selected</div>
                    : singleRow &&
                        <div className="d-flex gap-3 flex-wrap">
                            <EllipsisInfoLabel title={'Company Name:'} content={singleRow.mastercompanyname} maxChar='50'/>
                            <div className="vr"></div>
                            <EllipsisInfoLabel title={'Payout Cycle:'} content={singleRow.periodcyclestart + '-' + singleRow.periodcycleend + ' [' + singleRow.incentivefreq + ']'} maxChar='20'/>
                        </div>
                    }
                </div>
                {/* scrollable area: only this section scrolls so the inputs below stay visible */}
                <div className="flex-grow-1 overflow-auto" style={{minHeight: 0}}>
                    <div className="p-3 trail-section">
                        <div className="bg-white trail-card">
                            {isMulti ?
                                <OrderedDataTable RowComponent={RebatePayPeriodRowComponent} tableHeader={PAY_PERIOD_TABLE_HEADER} dataList={payPeriodRows} disableMaxTableHeight={true} />
                            : singleRow && <WorkflowCommentLog workflowid={singleRow.workflowid} disableHeight/>}
                        </div>
                    </div>
                </div>
                {/* pinned area: date picker, comment and actions are always visible */}
                <div className="bg-white w-100 flex-shrink-0 pay-period-pinned-panel">
                    <div className="p-3 pb-0">
                        <div className="offcanvas-details pb-2" style={{fontSize: '12px'}}>Pay Period</div>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                size='small'
                                value={payPeriodDate}
                                format="yyyy-MM-dd"
                                onChange={setPayPeriodDate}
                                emptyLabel='None'
                                views={['year', 'month', 'day']}
                                slotProps={{ textField: { size: 'small' } }}
                                slots={{
                                    openPickerIcon: () => <CalendarMonthIcon sx={{color: 'black'}}/>
                                }}
                                className='date-class'
                                sx={{width:"100%"}}
                            />
                        </LocalizationProvider>
                    </div>
                    <div className="p-3 pb-0">
                        <div className="offcanvas-details pb-2" style={{fontSize: '12px'}}>Comment</div>
                        <input className="w-100 input-text-field" value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder='Type comments here'></input>
                    </div>
                    <div className="d-flex justify-content-end p-3 offcanvas-footer">
                        <ButtonComponent tag={"Cancel"}
                            border={'#fcd800 1px solid'} background={'#fff'} color={'#001317'}
                            onButtonClick={closeModal}
                        />
                        <ApiRequestButton
                            onButtonClick={onSubmitClick}
                        />
                    </div>
                </div>
            </div>
        </CCOffcanvas>
    );
}

export default RebatePayoutEditModal;
